/**
 * Surface group property hooks - REACTIVE VERSION
 * Provides reactive property access for surface plane coordinates and tolerance
 *
 * This now uses the reactive property system which provides:
 * - Direct value access (property.value)
 * - Automatic updates to all subscribers (React + Potree)
 * - Automatic database persistence
 */

import { useCallback, useEffect } from 'react';
import { PropertyType } from '../../types';
// Import directly to avoid pulling in pusher.ts via barrel index (breaks Potree build)
import { useReactivePropertyManager } from '../../reactive/useReactivePropertyManager';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { PropertyValueData } from '@pointorama/potree-shared';
import { SurfaceKind } from 'shared-types';
import { registerSurfaceProperties } from './SurfacePropertyGetters';

// Re-export everything from the getters file so existing client imports still work
export { registerSurfaceProperties, getSurfacePlanePositionProperty, getSurfacePlaneNormalProperty, getSurfaceToleranceProperty, getSurfacePlaneRotationProperty, getSurfaceKindProperty } from './SurfacePropertyGetters';

// ============================================================================
// TYPES
// ============================================================================

type Coordinate = { x: number; y: number; z: number };

// ============================================================================
// REACTIVE PROPERTY ACCESS HOOK
// ============================================================================

/**
 * Custom hook to get reactive surface properties
 * Returns ReactiveProperty instances that can be used directly
 *
 * @param projectId - The project identifier
 * @returns Object with reactive property getters
 */
export const useSurfaceProperties = (projectId: string) => {
  const service = useReactivePropertyManager({ projectId });

  // Ensure properties are registered
  useEffect(() => {
    registerSurfaceProperties();
  }, []);

  /**
   * Get reactive property for surface plane position
   * Returns a ReactiveProperty that auto-updates and auto-saves
   */
  const getSurfacePlanePositionProperty = useCallback(
    (surfaceId: string): ReactiveProperty<Coordinate> => {
      return service.getProperty('surface_plane_position', surfaceId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
    },
    [service],
  );

  /**
   * Get reactive property for surface plane normal
   */
  const getSurfacePlaneNormalProperty = useCallback(
    (surfaceId: string): ReactiveProperty<Coordinate> => {
      return service.getProperty('surface_plane_normal', surfaceId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
    },
    [service],
  );

  /**
   * Get reactive property for surface tolerance
   */
  const getSurfaceToleranceProperty = useCallback(
    (surfaceId: string): ReactiveProperty<number> => {
      return service.getProperty('surface_tolerance', surfaceId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
    },
    [service],
  );

  /**
   * Get reactive property for surface plane rotation (around the normal)
   */
  const getSurfacePlaneRotationProperty = useCallback(
    (surfaceId: string): ReactiveProperty<number> => {
      return service.getProperty('surface_plane_rotation', surfaceId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
    },
    [service],
  );

  /**
   * Get reactive property for surface kind (SURFACE or SECTION)
   */
  const getSurfaceKindProperty = useCallback(
    (surfaceId: string): ReactiveProperty<SurfaceKind> => {
      return service.getProperty('surface_kind', surfaceId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<SurfaceKind>;
    },
    [service],
  );

  // ============================================================================
  // Batch setter for creating new surfaces (used in usePotreeEvents)
  // ============================================================================

  const setSurfaceProperties = useCallback(
    async (
      surfaceId: string,
      properties: {
        surfacePlanePosition?: Coordinate;
        surfacePlaneNormal?: Coordinate;
        surfaceTolerance?: number;
        surfacePlaneRotation?: number;
        surfaceKind?: SurfaceKind;
      },
    ): Promise<void> => {
      // Batch update all properties together to avoid race conditions
      type PropertyPair = [ReactiveProperty<PropertyValueData>, PropertyValueData];
      const propertyValuePairs: PropertyPair[] = [];

      if (properties.surfacePlanePosition) {
        const positionProperty = getSurfacePlanePositionProperty(surfaceId);
        propertyValuePairs.push([positionProperty as unknown as ReactiveProperty<PropertyValueData>, properties.surfacePlanePosition]);
      }

      if (properties.surfacePlaneNormal) {
        const normalProperty = getSurfacePlaneNormalProperty(surfaceId);
        propertyValuePairs.push([normalProperty as unknown as ReactiveProperty<PropertyValueData>, properties.surfacePlaneNormal]);
      }

      if (properties.surfaceTolerance !== undefined) {
        const toleranceProperty = getSurfaceToleranceProperty(surfaceId);
        propertyValuePairs.push([toleranceProperty as unknown as ReactiveProperty<PropertyValueData>, properties.surfaceTolerance]);
      }

      if (properties.surfacePlaneRotation !== undefined) {
        const rotationProperty = getSurfacePlaneRotationProperty(surfaceId);
        propertyValuePairs.push([rotationProperty as unknown as ReactiveProperty<PropertyValueData>, properties.surfacePlaneRotation]);
      }

      if (properties.surfaceKind !== undefined) {
        const kindProperty = getSurfaceKindProperty(surfaceId);
        propertyValuePairs.push([kindProperty as unknown as ReactiveProperty<PropertyValueData>, properties.surfaceKind]);
      }

      if (propertyValuePairs.length > 0) {
        await service.batchUpdateProperties(propertyValuePairs);
      }
    },
    [service, getSurfacePlanePositionProperty, getSurfacePlaneNormalProperty, getSurfaceToleranceProperty, getSurfacePlaneRotationProperty, getSurfaceKindProperty],
  );

  return {
    // Loading state from the reactive property manager
    loading: service.loading,

    // Reactive property getters
    getSurfacePlanePositionProperty,
    getSurfacePlaneNormalProperty,
    getSurfaceToleranceProperty,
    getSurfacePlaneRotationProperty,
    getSurfaceKindProperty,

    // Batch setter for surface creation
    setSurfacePropertiesBatch: setSurfaceProperties,
  };
};
